import React, { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useLanguage } from '../context/LanguageContext';
import { offices, contactInfo } from '../data/mock';
import { MapPin, Phone, Mail, Send, CheckCircle } from 'lucide-react';

const ContactPage = () => {
  const { t, language } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission
    setTimeout(() => {
      console.log('Form submitted:', formData);
      setSubmitted(true);
      setIsSubmitting(false);
      setTimeout(() => setSubmitted(false), 5000);
      setFormData({ name: '', email: '', phone: '', company: '', message: '' });
    }, 1500);
  };

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 transition-colors duration-300">
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-[#1a6b5c] to-[#0f4a3f]">
        <div className="absolute inset-0 overflow-hidden">
          {[...Array(15)].map((_, i) => (
            <div
              key={i}
              className="absolute w-2 h-2 bg-white/10 rounded-full animate-float"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${i * 0.3}s`,
              }}
            />
          ))}
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold italic text-white mb-4 animate-fade-in">
            {t('contact.title')}
          </h1>
          <p className="text-[#3ECDA3] text-lg animate-fade-in" style={{ animationDelay: '0.2s' }}>
            {t('contact.subtitle')}
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
            {/* Contact Form */}
            <div className="animate-slide-left">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">
                {t('contact.formTitle')}
              </h2>
              
              {submitted && (
                <div className="mb-6 p-4 bg-[#3ECDA3]/10 border border-[#3ECDA3] rounded-lg text-[#1a6b5c] dark:text-[#3ECDA3] flex items-center gap-3 animate-fade-in">
                  <CheckCircle className="w-5 h-5" />
                  {t('contact.success')}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('contact.name')} *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-[#3ECDA3] focus:border-transparent dark:bg-gray-800 dark:text-white outline-none transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('contact.email')} *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-[#3ECDA3] focus:border-transparent dark:bg-gray-800 dark:text-white outline-none transition-all"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('contact.phone')}
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-[#3ECDA3] focus:border-transparent dark:bg-gray-800 dark:text-white outline-none transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('contact.company')}
                    </label>
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-[#3ECDA3] focus:border-transparent dark:bg-gray-800 dark:text-white outline-none transition-all"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('contact.message')} *
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-[#3ECDA3] focus:border-transparent dark:bg-gray-800 dark:text-white outline-none transition-all resize-none"
                  ></textarea>
                </div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="inline-flex items-center gap-2 bg-[#3ECDA3] text-white px-8 py-4 rounded-lg font-medium hover:bg-[#35b892] hover:shadow-lg transition-all duration-300 disabled:opacity-50"
                >
                  {isSubmitting ? (
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <Send size={18} />
                  )}
                  {t('contact.send')}
                </button>
              </form>
            </div>

            {/* Contact Info */}
            <div className="animate-slide-right">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">
                {t('contact.infoTitle')}
              </h2>

              {/* Phone & Email */}
              <div className="mb-12 space-y-4">
                {contactInfo.phones.map((phone, index) => (
                  <a 
                    key={index} 
                    href={`tel:${phone.replace(/\s/g, '')}`} 
                    className={`flex items-center gap-4 group ${index === 0 ? 'bg-[#3ECDA3]/10 p-3 rounded-xl border border-[#3ECDA3]/30' : ''}`}
                  >
                    <div className={`${index === 0 ? 'bg-[#3ECDA3]/20' : 'bg-[#3ECDA3]/10'} p-3 rounded-lg group-hover:bg-[#3ECDA3]/20 transition-colors`}>
                      <Phone className={`w-5 h-5 text-[#3ECDA3] ${index === 0 ? 'w-6 h-6' : ''}`} />
                    </div>
                    <div>
                      <span className={`group-hover:text-[#3ECDA3] transition-colors ${index === 0 ? 'text-[#3ECDA3] font-bold text-lg' : 'text-gray-700 dark:text-gray-300'}`}>{phone}</span>
                      {index === 0 && <p className="text-[#3ECDA3]/70 text-xs font-medium">Principal</p>}
                    </div>
                  </a>
                ))}
                <a href={`mailto:${contactInfo.email}`} className="flex items-center gap-4 group">
                  <div className="bg-[#3ECDA3]/10 p-3 rounded-lg group-hover:bg-[#3ECDA3]/20 transition-colors">
                    <Mail className="w-5 h-5 text-[#3ECDA3]" />
                  </div>
                  <span className="text-gray-700 dark:text-gray-300 group-hover:text-[#3ECDA3] transition-colors">{contactInfo.email}</span>
                </a>
              </div>

              {/* Offices */}
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-6">
                {t('contact.officesTitle')}
              </h3>
              <div className="space-y-6">
                {offices.map((office, index) => (
                  <div 
                    key={office.id} 
                    className="flex gap-4 hover:translate-x-2 transition-transform duration-300"
                    style={{ animationDelay: `${index * 100}ms` }}
                  >
                    <div className="bg-[#3ECDA3]/10 p-3 rounded-lg h-fit">
                      <MapPin className="w-5 h-5 text-[#3ECDA3]" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">{office.city}</p>
                      <p className="text-gray-600 dark:text-gray-400 text-sm">{office.address}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;
